import type {
	BaseItemSchema,
	Grading,
	OriginalItemTypes,
} from "@memorang/types";
import { WFD_SPELLING_VARIANTS } from "./dictation-spelling-variants";

export const isWriteFromDictation = (itemType?: OriginalItemTypes): boolean =>
	itemType === "WFDL" || itemType === "WFDL_CORE";

export const getAnswerKeyWords = (scoring?: Grading): string[] | undefined =>
	scoring && "type" in scoring && scoring.type === "ANSWER_KEY_TEXT"
		? scoring.answerKey.map((entry) => entry.word)
		: undefined;

export const getDictationAnswerKeyWords = (
	item: BaseItemSchema,
): string[] | undefined =>
	isWriteFromDictation(item.metadata?.type)
		? getAnswerKeyWords(item.scoring)
		: undefined;

const WFD_NORMALIZED_MAX = 5;

export const normalizeWord = (word: string) =>
	word.toLowerCase().replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, "");

export type DictationMatchResult = {
	matchedTokens: (string | null)[];
	unmatchedTokens: string[];
};

export const matchDictationWords = (
	answerKeyWords: string[],
	responseText: string,
	spellingVariants: Readonly<
		Record<string, readonly string[]>
	> = WFD_SPELLING_VARIANTS,
): DictationMatchResult => {
	const tokens = responseText
		.split(/\s+/)
		.map((raw) => ({ raw, word: normalizeWord(raw), used: false }))
		.filter((token) => token.word);
	const consume = (word: string): string | null => {
		const token = tokens.find(
			(candidate) => !candidate.used && candidate.word === word,
		);
		if (!token) {
			return null;
		}
		token.used = true;
		return token.raw;
	};
	const matchKeyWord = (keyWord: string): string | null => {
		const word = normalizeWord(keyWord);
		const variants = Object.hasOwn(spellingVariants, word)
			? (spellingVariants[word] ?? [])
			: [];
		for (const candidate of [word, ...variants]) {
			const matchedToken = consume(candidate);
			if (matchedToken !== null) {
				return matchedToken;
			}
		}
		return null;
	};
	return {
		matchedTokens: answerKeyWords.map((keyWord) => matchKeyWord(keyWord)),
		unmatchedTokens: tokens
			.filter((token) => !token.used)
			.map((token) => token.raw),
	};
};

export const countCorrectDictationWords = (
	answerKeyWords: string[],
	responseText: string,
	spellingVariants: Readonly<
		Record<string, readonly string[]>
	> = WFD_SPELLING_VARIANTS,
): number =>
	matchDictationWords(
		answerKeyWords,
		responseText,
		spellingVariants,
	).matchedTokens.filter((token) => token !== null).length;

// Kept unrounded: per-skill GSE sums accumulate the fractions and round once at
// the end. `scoring.maxNormalizedScore` is deliberately ignored; it is null on
// every production WFD item and cannot be trusted.
export const toNormalizedDictationScore = (
	numCorrectWords: number,
	numAnswerKeyWords: number,
): number =>
	numAnswerKeyWords === 0
		? 0
		: (numCorrectWords / numAnswerKeyWords) * WFD_NORMALIZED_MAX;
